import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
    Bars3Icon,
    XMarkIcon,
    MagnifyingGlassIcon,
    ChevronDownIcon,
    AcademicCapIcon,
    BuildingLibraryIcon,
    ChevronRightIcon,
    ClockIcon,
    UserGroupIcon,
    VideoCameraIcon,
    PhoneIcon,
    EnvelopeIcon,
    MapPinIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline'
import {
    FaceSmileIcon, // Using as placeholder for social icons if dedicated ones aren't imported
} from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { degreeAPI, universityAPI, courseAPI, approvalAPI, specializationAPI } from '../../services/api'
import SearchModal from '../Search/SearchModal'
import EnquiryModal from '../Enquiry/EnquiryModal'

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isScrolled, setIsScrolled] = useState(false)
    const [isSearchOpen, setIsSearchOpen] = useState(false)
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)
    const [enquiryType, setEnquiryType] = useState('consultation')
    const [activeFlyout, setActiveFlyout] = useState(null)
    const [activeCourseLevel, setActiveCourseLevel] = useState('Undergraduate (UG)')
    const [activeUniversityDegree, setActiveUniversityDegree] = useState(null)
    const location = useLocation()

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10)
        }

        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        setIsMenuOpen(false)
        setActiveFlyout(null)
    }, [location])

    // Fetch data for mega menus
    const { data: degreesData } = useQuery({
        queryKey: ['degrees'],
        queryFn: () => degreeAPI.getDegrees(),
        staleTime: 1000 * 60 * 5 // 5 minutes
    })

    const { data: levelsData } = useQuery({
        queryKey: ['levels'],
        queryFn: () => courseAPI.getLevels(),
        staleTime: 1000 * 60 * 5
    })

    const { data: approvalsData } = useQuery({
        queryKey: ['approvals'],
        queryFn: () => approvalAPI.getApprovals(),
        staleTime: 1000 * 60 * 5
    })

    const { data: specializationsData } = useQuery({
        queryKey: ['specializations', activeUniversityDegree],
        queryFn: () => specializationAPI.getSpecializations({ degree: activeUniversityDegree }),
        enabled: !!activeUniversityDegree,
        staleTime: 1000 * 60 * 5
    })

    const levels = levelsData?.data?.levels || ['Undergraduate (UG)', 'Postgraduate (PG)', 'Diploma', 'Certificate']
    const degrees = degreesData?.data?.degrees || []
    const approvals = approvalsData?.data?.approvals || []
    const specializations = specializationsData?.data?.specializations || []

    const filteredDegrees = degrees.filter(d => d.level === activeCourseLevel)

    // Set initial active university degree when degrees load
    useEffect(() => {
        if (degrees.length > 0 && !activeUniversityDegree) {
            setActiveUniversityDegree(degrees[0]._id)
        }
    }, [degrees, activeUniversityDegree])

    const navigation = [
        { name: 'Home', href: '/' },
        {
            name: 'Courses',
            href: '/courses',
            hasMegaMenu: true,
            type: 'courses'
        },
        {
            name: 'Top Universities',
            href: '/universities',
            hasMegaMenu: false,
            type: 'universities'
        },
        { name: 'Blogs', href: '/blogs' },
        { name: 'Contact', href: '/contact' },
    ]

    return (
        <>
            <header
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled
                    ? 'bg-white/80 backdrop-blur-xl shadow-lg border-b border-gray-100'
                    : 'bg-transparent'
                    }`}
                onMouseLeave={() => setActiveFlyout(null)}
            >
                {/* Top Bar - Hidden on scroll for focus, but could be kept if desired */}
                <div className={`hidden lg:block bg-gradient-to-r from-primary-900 to-primary-700 text-white transition-all duration-300 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'}`}>
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between text-xs font-medium">
                        <div className="flex items-center space-x-6">
                            <a href="tel:+919810530354" className="flex items-center hover:text-accent-300 transition-colors">
                                <PhoneIcon className="w-3.5 h-3.5 mr-1.5" />
                                <span>+91-9810530354</span>
                            </a>
                            <a href="mailto:admission@mymargdarshak.com" className="flex items-center hover:text-accent-300 transition-colors">
                                <EnvelopeIcon className="w-3.5 h-3.5 mr-1.5" />
                                <span>admission@mymargdarshak.com</span>
                            </a>
                            <div className="flex items-center text-primary-200">
                                <MapPinIcon className="w-3.5 h-3.5 mr-1.5" />
                                <span>Ghaziabad, India</span>
                            </div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <span className="text-primary-300 mr-2">Follow us:</span>
                            <div className="flex items-center space-x-4">
                                <a href="https://www.facebook.com/profile.php?id=61558479383234" target="_blank" rel="noopener noreferrer" className="hover:text-accent-300 transition-colors" title="Facebook">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </a>
                                <a href="https://x.com/mymargdarshak" target="_blank" rel="noopener noreferrer" className="hover:text-accent-300 transition-colors" title="X (Twitter)">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                </a>
                                <a href="https://www.instagram.com/mymargdarshak/" target="_blank" rel="noopener noreferrer" className="hover:text-accent-300 transition-colors" title="Instagram">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.791-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.209-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                                    </svg>
                                </a>
                                <a href="https://www.linkedin.com/company/109284591" target="_blank" rel="noopener noreferrer" className="hover:text-accent-300 transition-colors" title="LinkedIn">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <nav className={`container mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-300 ${isScrolled ? 'h-16 lg:h-16' : 'h-16 lg:h-24'}`}>
                    <div className="flex justify-between items-center h-full">
                        {/* Logo */}
                        <Link to="/" className="flex items-center space-x-2 group">
                            <div className="relative">
                                <img
                                    src="/marglogo.png"
                                    alt="MyMargdarshak Logo"
                                    className={`object-contain transition-all duration-300 ${isScrolled ? 'h-10 sm:h-10' : 'h-12 sm:h-14'}`}
                                />
                                {!isScrolled && (
                                    <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-accent-500 rounded-full animate-pulse border-2 border-white"></div>
                                )}
                            </div>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
                            {navigation.map((item) => (
                                <div
                                    key={item.name}
                                    className="relative h-full flex items-center"
                                    onMouseEnter={() => item.hasMegaMenu ? setActiveFlyout(item.type) : setActiveFlyout(null)}
                                >
                                    <Link
                                        to={item.href}
                                        className={`group relative px-4 py-2 flex items-center text-[15px] font-bold transition-all duration-300 rounded-full ${(location.pathname === item.href || activeFlyout === item.type)
                                            ? 'text-accent-600 bg-accent-50'
                                            : isScrolled ? 'text-gray-700 hover:text-accent-600 hover:bg-gray-50' : 'text-gray-800 hover:text-accent-600 hover:bg-white/50'
                                            }`}
                                    >
                                        <span>{item.name}</span>
                                        {item.hasMegaMenu && (
                                            <ChevronDownIcon className={`ml-1.5 w-4 h-4 transition-transform duration-300 ${activeFlyout === item.type ? 'rotate-180' : ''}`} />
                                        )}
                                        {/* Dynamic Indicator */}
                                        {(location.pathname === item.href) && (
                                            <motion.div
                                                layoutId="nav-indicator"
                                                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-accent-600 rounded-full"
                                            />
                                        )}
                                    </Link>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden lg:flex items-center space-x-3">
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className={`p-2.5 rounded-xl transition-all duration-300 ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-900 hover:bg-white/80'}`}
                                title="Search"
                            >
                                <MagnifyingGlassIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => {
                                    setEnquiryType('consultation')
                                    setIsEnquiryOpen(true)
                                }}
                                className="relative group overflow-hidden bg-accent-600 text-white px-7 py-3 rounded-xl text-sm font-black hover:shadow-[0_10px_20px_-10px_rgba(249,115,22,0.5)] transition-all duration-300"
                            >
                                <span className="relative z-10">Free Counselling</span>
                                <div className="absolute inset-0 bg-gradient-to-r from-accent-700 to-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            </button>
                        </div>

                        {/* Mobile menu button */}
                        <div className="lg:hidden flex items-center space-x-1">
                            <button
                                onClick={() => setIsSearchOpen(true)}
                                className={`p-2 rounded-lg ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-900 hover:bg-white/50'}`}
                            >
                                <MagnifyingGlassIcon className="w-5 h-5" />
                            </button>
                            <button
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`p-2 rounded-lg ${isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-gray-900 hover:bg-white/50'}`}
                            >
                                {isMenuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </nav>

                {/* Mega Menus */}
                <AnimatePresence>
                    {activeFlyout === 'courses' && (
                        <motion.div
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 15 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                            className="hidden lg:block absolute top-full left-0 w-full bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border-t border-gray-100 overflow-hidden"
                        >
                            {/* Decorative background elements */}
                            <div className="absolute top-0 right-0 w-96 h-96 bg-primary-50 rounded-full blur-3xl pointer-events-none -mr-48 -mt-48"></div>
                            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent-50 rounded-full blur-3xl pointer-events-none -ml-48 -mb-48"></div>

                            <div className="container mx-auto flex relative z-10">
                                {/* Sidebar Categories */}
                                <div className="w-1/4 bg-slate-50/80 p-10 border-r border-gray-100 max-h-[600px] overflow-y-auto scrollbar-hide">
                                    <div className="flex items-center space-x-2 mb-8">
                                        <div className="w-1.5 h-6 bg-accent-500 rounded-full"></div>
                                        <h3 className="text-[11px] font-black text-gray-500 uppercase tracking-[0.2em]">Explore Levels</h3>
                                    </div>
                                    <div className="space-y-4">
                                        {levels.map((level) => (
                                            <button
                                                key={level}
                                                onMouseEnter={() => setActiveCourseLevel(level)}
                                                className={`w-full flex items-center justify-between px-6 py-5 rounded-2xl text-sm font-black transition-all duration-300 ${activeCourseLevel === level
                                                    ? 'bg-white text-accent-600 shadow-xl shadow-accent-500/10 translate-x-3 ring-2 ring-accent-100/50'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50 hover:translate-x-1'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center mr-4 transition-all duration-300 ${activeCourseLevel === level ? 'bg-accent-600 text-white shadow-lg shadow-accent-500/30 scale-110' : 'bg-white text-gray-400 shadow-sm border border-gray-100'}`}>
                                                        <AcademicCapIcon className="w-6 h-6" />
                                                    </div>
                                                    <span className="tracking-tight">{level.split(' (')[0]}</span>
                                                </div>
                                                <ChevronRightIcon className={`w-5 h-5 transition-all duration-300 ${activeCourseLevel === level ? 'opacity-100' : 'opacity-0 -translate-x-2'}`} />
                                            </button>
                                        ))}
                                    </div>

                                    {/* Counselling Banner in Sidebar */}
                                    <div className="mt-16 p-8 rounded-[2rem] bg-gradient-to-br from-primary-950 via-primary-900 to-primary-800 text-white relative overflow-hidden shadow-2xl">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-accent-500/20 rounded-full blur-2xl -mr-12 -mt-12"></div>
                                        <div className="relative z-10">
                                            <h4 className="text-base font-black text-white mb-3 tracking-tight">Need Career Advice?</h4>
                                            <p className="text-xs text-primary-100 mb-6 font-medium leading-relaxed text-white opacity-90">Book a free session with our top education experts today.</p>
                                            <button
                                                onClick={() => {
                                                    setEnquiryType('consultation')
                                                    setIsEnquiryOpen(true)
                                                }}
                                                className="w-full py-3.5 bg-accent-500 hover:bg-white text-white hover:text-accent-600 rounded-xl text-xs font-black transition-all duration-300 shadow-lg shadow-accent-600/20"
                                            >
                                                Book Free Counselling
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Content Grid */}
                                <div className="w-3/4 p-12 bg-white/60 overflow-y-auto max-h-[600px] scrollbar-hide">
                                    <div className="flex items-center justify-between mb-12 pb-10 border-b border-gray-100">
                                        <div>
                                            <h3 className="text-5xl font-black text-gray-900 tracking-tight leading-tight">
                                                {activeCourseLevel.split(' (')[0]} <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 via-accent-600 to-primary-600 animate-gradient-x">Specializations</span>
                                            </h3>
                                        </div>
                                        <Link
                                            to="/courses"
                                            className="group flex items-center bg-gradient-to-r from-primary-600 to-primary-700 hover:from-accent-600 hover:to-accent-700 text-white px-10 py-5 rounded-2xl font-black text-sm shadow-[0_10px_30px_-10px_rgba(30,58,138,0.5)] transition-all duration-300 hover:-translate-y-1"
                                        >
                                            View All Courses
                                            <ChevronRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1.5 transition-transform" />
                                        </Link>
                                    </div>

                                    <div className="grid grid-cols-3 gap-8">
                                        {filteredDegrees.length > 0 ? (
                                            filteredDegrees.slice(0, 9).map((degree) => (
                                                <Link
                                                    key={degree._id}
                                                    to={`/courses?level=${encodeURIComponent(activeCourseLevel)}&degrees=${degree._id}`}
                                                    className="group h-full"
                                                >
                                                    <div className="h-full bg-gradient-to-br from-white to-gray-50/50 border-2 border-gray-100 p-6 rounded-[1.5rem] shadow-sm hover:shadow-2xl hover:shadow-accent-500/10 hover:border-accent-100 transition-all duration-500 relative overflow-hidden flex flex-col">
                                                        {/* Glossy overlay */}
                                                        <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/40 to-white/0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                                                        <div className="w-12 h-12 rounded-xl bg-accent-50 flex items-center justify-center text-accent-600 mb-5 group-hover:bg-accent-600 group-hover:text-white transition-all duration-500 shadow-inner group-hover:scale-110 group-hover:rotate-3">
                                                            <AcademicCapIcon className="w-6 h-6" />
                                                        </div>

                                                        <h4 className="font-black text-gray-900 text-xl leading-tight group-hover:text-accent-700 transition-colors mb-3 tracking-tight">{degree.name}</h4>
                                                        <p className="text-sm text-gray-600 font-medium line-clamp-3 leading-relaxed mb-8 group-hover:text-gray-700 transition-colors">Accelerate your career with a specialized {degree.name} degree from a top-tier UCG recognized university.</p>

                                                        <div className="mt-auto pt-4 flex items-center text-[11px] font-black text-gray-500 uppercase tracking-[0.2em] group-hover:text-accent-600 group-hover:translate-x-1.5 transition-all">
                                                            <span>Explore Course</span>
                                                            <ArrowRightIcon className="w-4 h-4 ml-2.5 transition-all" />
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))
                                        ) : (
                                            <div className="col-span-3 flex flex-col items-center justify-center py-32 bg-slate-50/50 rounded-[3rem] border-2 border-dashed border-gray-200">
                                                <div className="w-24 h-24 bg-white rounded-3xl flex items-center justify-center shadow-xl shadow-gray-200/50 mb-8 border border-gray-100">
                                                    <AcademicCapIcon className="w-12 h-12 text-gray-300" />
                                                </div>
                                                <h4 className="text-2xl font-black text-gray-900 tracking-tight">No Courses Found</h4>
                                                <p className="text-gray-500 font-semibold max-w-xs text-center mt-3 leading-relaxed">We're currently updating our course list for this level. Stay tuned!</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}


                </AnimatePresence>

                {/* Mobile Navigation */}
                <AnimatePresence>
                    {isMenuOpen && (
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed inset-0 z-[100] lg:hidden bg-white/100 flex flex-col pt-16"
                        >
                            {/* Mobile Top Header */}
                            <div className="absolute top-0 left-0 right-0 h-16 px-4 flex items-center justify-between border-b border-gray-100 bg-white">
                                <img src="/marglogo.png" alt="Margdarshak" className="h-8 w-auto" />
                                <button onClick={() => setIsMenuOpen(false)} className="p-2 text-gray-800 bg-gray-100 rounded-full">
                                    <XMarkIcon className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="flex-1  px-6 py-10 space-y-8" style={{ "position": "sticky", "background": "white" }}>
                                <div className="space-y-4">
                                    <div className="flex items-center space-x-2 mb-4">
                                        <div className="w-1 h-4 bg-accent-500 rounded-full"></div>
                                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Main Menu</h3>
                                    </div>
                                    {navigation.map((item, idx) => (
                                        <motion.div
                                            key={item.name}
                                            initial={{ x: 20 }}
                                            animate={{ x: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                        >
                                            <Link
                                                to={item.href}
                                                className={`flex items-center justify-between group p-3 rounded-xl transition-all ${location.pathname === item.href
                                                    ? 'text-accent-600 bg-accent-50'
                                                    : 'text-gray-900 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <span className="text-3xl font-black tracking-tight">{item.name}</span>
                                                <ChevronRightIcon className="w-6 h-6 text-gray-300" />
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>

                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            {/* Search Modal */}
            <SearchModal
                isOpen={isSearchOpen}
                onClose={() => setIsSearchOpen(false)}
            />

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={isEnquiryOpen}
                onClose={() => setIsEnquiryOpen(false)}
                enquiryType={enquiryType}
            />
        </>
    )
}

export default Header
